/**
 * Codec family selection for HLS ladders that carry several video codecs.
 *
 * Family priority is AV1 > HEVC > VP9 > H.264, the same order as
 * widevine-policy.ts. A family is a candidate when MediaSource can decode at
 * least one of its renditions. Advanced families are additionally gated by a
 * Media Capabilities verdict obtained once per page, before any manifest is
 * parsed, at the class of resolution automatic playback intends to start on
 * (HD, or UHD above 1080p): hardware (power-efficient) decoding is always
 * acceptable, and on desktop software decoding the browser predicts to be
 * smooth is acceptable too. Hardware is not also required to be "smooth"
 * because iOS reports every HEVC configuration as power-efficient but not
 * smooth. H.264 is never probed. Probing up front keeps manifest handling
 * synchronous: hls.js only honours a start level that is set before it
 * requests the first fragment.
 *
 * Levels are never removed from hls.js for codec reasons. hls.js keeps
 * automatic adaptation inside the codec set it started on, so choosing the
 * start level from the selected family keeps auto playback and the quality
 * menu on the same codec for the whole session, while manual selection of any
 * rendition stays possible.
 */

export type CodecFamily = 'av01' | 'hvc1' | 'vp09' | 'avc1';
export type AdvancedCodecFamily = Exclude<CodecFamily, 'avc1'>;

export const CODEC_FAMILY_PRIORITY: readonly CodecFamily[] = [
  'av01',
  'hvc1',
  'vp09',
  'avc1',
];

export interface CodecLevel {
  videoCodec?: string;
  audioCodec?: string;
  codecSet?: string;
  width?: number;
  height?: number;
  bitrate?: number;
  frameRate?: number;
}

export interface CodecSupportChecker {
  isTypeSupported(type: string): boolean;
}

export interface MediaCapabilitiesLike {
  decodingInfo(
    configuration: MediaDecodingConfiguration,
  ): Promise<MediaCapabilitiesInfo>;
}

/** Whether a family decodes acceptably at HD (up to 1080p) and at UHD. */
export interface CodecCapability {
  hd: boolean;
  uhd: boolean;
}

/** Families missing from the table could not be probed and are accepted. */
export type CodecCapabilityTable = Partial<
  Record<AdvancedCodecFamily, CodecCapability>
>;

export interface CodecSelectionOptions {
  /**
   * Given the indices of one family's supported levels, return the index of
   * the level automatic playback would start on, or -1 when unknown (the HD
   * verdict is then used).
   */
  pickProbeLevel: (familyLevelIndices: number[]) => number;
}

export interface CodecSelection {
  codecFamily: CodecFamily | null;
  /** Indices of the selected family's supported levels, in input order. */
  levelIndices: number[];
  /**
   * True when no advanced family passed its capability verdict and there is
   * no H.264 fallback, so the most preferred family was kept for software
   * decode.
   */
  softwareFallback: boolean;
}

// Representative Main-profile renditions per family. Hardware decoders that
// handle these handle the ladder's actual profiles as well; the verdict is
// about the device class, not an exact rendition.
const CAPABILITY_PROBES: Record<
  AdvancedCodecFamily,
  Record<keyof CodecCapability, MediaDecodingConfiguration['video']>
> = {
  av01: {
    hd: probeVideo('av01.0.08M.08', 1920, 1080, 5_000_000),
    uhd: probeVideo('av01.0.12M.08', 3840, 2160, 16_000_000),
  },
  hvc1: {
    hd: probeVideo('hvc1.1.6.L120.B0', 1920, 1080, 6_000_000),
    uhd: probeVideo('hvc1.1.6.L150.B0', 3840, 2160, 20_000_000),
  },
  vp09: {
    hd: probeVideo('vp09.00.40.08', 1920, 1080, 6_000_000),
    uhd: probeVideo('vp09.00.50.08', 3840, 2160, 20_000_000),
  },
};

function probeVideo(
  codec: string,
  width: number,
  height: number,
  bitrate: number,
): MediaDecodingConfiguration['video'] {
  return {
    contentType: `video/mp4; codecs="${codec}"`,
    width,
    height,
    bitrate,
    framerate: 30,
  };
}

export function getCodecFamily(level: CodecLevel): CodecFamily | null {
  const codec = `${level.videoCodec ?? ''} ${level.codecSet ?? ''}`
    .toLowerCase();
  if (codec.includes('av01')) return 'av01';
  if (codec.includes('hvc1') || codec.includes('hev1')) return 'hvc1';
  if (codec.includes('vp09') || codec.includes('vp9')) return 'vp09';
  if (codec.includes('avc1') || codec.includes('avc3')) return 'avc1';
  return null;
}

/** MIME string for MediaSource.isTypeSupported(). */
export function getCodecString(level: CodecLevel): string | null {
  if (!level.videoCodec) return null;
  const codecs = level.audioCodec
    ? `${level.videoCodec}, ${level.audioCodec}`
    : level.videoCodec;
  return `video/mp4; codecs="${codecs}"`;
}

export function isCodecSupported(
  level: CodecLevel,
  checker: CodecSupportChecker | null | undefined,
): boolean {
  if (!checker) return false;
  const codecString = getCodecString(level);
  if (!codecString) return false;
  try {
    return checker.isTypeSupported(codecString);
  } catch (_error) {
    return false;
  }
}

/** The shorter side, so portrait renditions rank by their real resolution. */
export function getDisplayHeight(level: CodecLevel): number {
  const { width = 0, height = 0 } = level;
  if (width > 0 && height > 0) return Math.min(width, height);
  return height > 0 ? height : 0;
}

/**
 * Ask Media Capabilities, once, how each advanced family decodes on this
 * device: acceptable when supported and power-efficient (hardware), or, unless
 * `requirePowerEfficient` (mobile), when supported and predicted smooth in
 * software. A family whose probe is unavailable or fails is left out of the
 * table, which selectCodecFamily() treats as acceptable: MediaSource support
 * is checked separately and an unanswered probe must not veto a codec.
 */
export async function probeCodecCapabilities(
  mediaCapabilities: MediaCapabilitiesLike | null | undefined,
  requirePowerEfficient = false,
): Promise<CodecCapabilityTable> {
  const table: CodecCapabilityTable = {};
  if (!mediaCapabilities?.decodingInfo) return table;

  const acceptable = async (video: MediaDecodingConfiguration['video']) => {
    const info = await mediaCapabilities.decodingInfo({
      type: 'media-source',
      video,
    });
    return info.supported &&
      (info.powerEfficient || (!requirePowerEfficient && info.smooth));
  };

  await Promise.all(
    (Object.keys(CAPABILITY_PROBES) as AdvancedCodecFamily[]).map(
      async (family) => {
        try {
          const [hd, uhd] = await Promise.all([
            acceptable(CAPABILITY_PROBES[family].hd),
            acceptable(CAPABILITY_PROBES[family].uhd),
          ]);
          table[family] = { hd, uhd };
        } catch (_error) {
          // Leave the family unprobed.
        }
      },
    ),
  );
  return table;
}

/**
 * Pick the codec family for automatic playback and the quality menu.
 *
 * Families are tried in priority order. An advanced family is selected when
 * the capability table accepts it at the resolution class of its start
 * rendition (or when the table has no verdict for it); H.264 is selected as
 * soon as it is reached. A ladder whose advanced families are all rejected
 * and that has no H.264 keeps its most preferred family (software decode
 * beats no playback). A ladder with a single supported family is never
 * gated.
 */
export function selectCodecFamily(
  levels: readonly CodecLevel[] | null | undefined,
  checker: CodecSupportChecker | null | undefined,
  capabilities: CodecCapabilityTable | null | undefined,
  options: CodecSelectionOptions,
): CodecSelection {
  const supported = new Map<CodecFamily, number[]>();
  (levels ?? []).forEach((level, index) => {
    const family = getCodecFamily(level);
    if (!family || !isCodecSupported(level, checker)) return;
    supported.set(family, [...(supported.get(family) ?? []), index]);
  });
  const families = CODEC_FAMILY_PRIORITY.filter((family) =>
    supported.has(family)
  );
  if (families.length === 0) {
    return { codecFamily: null, levelIndices: [], softwareFallback: false };
  }

  for (const family of families) {
    const levelIndices = supported.get(family)!;
    if (family === 'avc1' || families.length === 1) {
      return { codecFamily: family, levelIndices, softwareFallback: false };
    }
    const verdict = capabilities?.[family];
    const startLevel = levels![options.pickProbeLevel(levelIndices)];
    const uhd = startLevel ? getDisplayHeight(startLevel) > 1080 : false;
    if (!verdict || (uhd ? verdict.uhd : verdict.hd)) {
      return { codecFamily: family, levelIndices, softwareFallback: false };
    }
  }

  return {
    codecFamily: families[0],
    levelIndices: supported.get(families[0])!,
    softwareFallback: true,
  };
}
